import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Collapse, DialogActions, DialogContent, Grid } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, TMainVisualFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider, { RHFSelect, RHFSwitch } from 'src/components/hook-form';
import RHFTextFieldWithColor from 'src/components/hook-form/RHFTextFieldWithColor';
import RHFUploadImage from 'src/components/hook-form/uploadImage/RHFUploadImage';
import Iconify from 'src/components/iconify';
import Preview from 'src/components/preview/Preview';
import useGetGuideInfo from 'src/hooks/useGetGuideInfo';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { ContentButtonAction } from 'src/layouts/form/FormButton';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { useStores } from 'src/models';
import { mainVisualApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import mainVisualFormValidationSchema from 'src/static/yup/mainVisualFormValidationSchema';
import { getMainVisualFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import { separateFilesAndPaths } from 'src/utils/separateFilesAndPaths';

interface IMainVisualFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: any;
}

export const MainVisualFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IMainVisualFormDialogProps) => {
    const { t } = useTranslation();

    const handleResponse = useHandleResponse();
    const { alertStore, loadingStore } = useStores();
    const { formId } = useDialogContext();
    const { getLang } = useGetSelectOption();
    const { getMainVisualAreas } = useGetGuideInfo();
    const defaultColor = '#000';

    const [isGuideOpen, setIsGuideOpen] = useState(true);
    const [files, setFiles] = useState<Array<File | string>>([]);
    const [colors, setColors] = useState({
      mainText: selectedData?.mainTextColor || defaultColor,
      subText: selectedData?.subTextColor || defaultColor,
    });

    const methods = useForm<TMainVisualFormData>({
      mode: 'onChange',
      resolver: yupResolver(mainVisualFormValidationSchema),
      defaultValues: getMainVisualFormDefaultValues(selectedData),
    });

    useEffect(() => {
      setFiles(selectedData?.bgPath || []);
    }, []);

    // 폼 데이터 실시간 preview 반영을 위함
    const [mainText, subText] = methods.watch(['mainText', 'subText']);

    // previewValues를 실시간 값으로 업데이트
    const currentPreviewValues = {
      mainText: mainText || '',
      subText: subText || '',
      mainTextColor: colors.mainText,
      subTextColor: colors.subText,
      bgPath: files || [],
    };

    // 등록 / 수정 함수
    const onValid = async (data: TMainVisualFormData) => {
      try {
        loadingStore.setLoading(true);
        const isCreate = formType === FormType.CREATE;
        // `bgPath` 배열에서 파일과 경로 분리
        const { files: uploadFiles, paths: existingPaths } = separateFilesAndPaths(files || []);

        // 삭제된 경로 추출
        const originalPaths = selectedData?.bgPath || [];
        const deleteFilesPath = originalPaths.filter(
          (path: string) => !existingPaths.includes(path),
        );

        let formData: TMainVisualFormData = {
          ...(uploadFiles.length > 0 && { files: uploadFiles }),
          ...data,
        };

        if (!isCreate) {
          formData = {
            ...formData,
            // 텍스트 데이터 유무에 따라 색상 데이터 포함
            ...(data.mainText
              ? { mainText: data.mainText, mainTextColor: data.mainTextColor }
              : { mainText: '', mainTextColor: '' }),
            ...(data.subText
              ? { subText: data.subText, subTextColor: data.subTextColor }
              : { subText: '', subTextColor: '' }),
            // 비디오 & 이미지
            ...(uploadFiles.length > 0 && { files: uploadFiles }),
            ...(existingPaths.length > 0 && { bgPath: existingPaths }),
            ...(deleteFilesPath.length > 0 && { deleteFilesPath }),
          };
        }

        const result = await (isCreate
          ? mainVisualApi.post(formData)
          : mainVisualApi.put(selectedData?.visualSid, formData));
        if (await result) {
          handleResponse('완료되었습니다.', '실패되었습니다.', onSubmitSuccess);
        }
      } catch (error) {
        console.log('🚀 ~ onValid ~ error:', error);
      } finally {
        loadingStore.setLoading(false);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (selectedData?.visualSid) {
          mainVisualApi
            .remove(selectedData?.visualSid)
            .then(() =>
              handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
            );
        }
      });
      alertStore.setProps({
        open: true,
        title: '컨텐츠 삭제',
        content: '해당 컨텐츠를 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    // 파일 변경 핸들러 수정
    const handleFileChange = (newFiles: Array<File | string>) => {
      setFiles(newFiles);
    };

    // 토글
    const handleToggle = () => {
      setIsGuideOpen(!isGuideOpen);
    };

    // 색상 변경 핸들러
    const handleColorChange = (fieldName: string) => (color: string) => {
      setColors((prev) => ({
        ...prev,
        [fieldName]: color,
      }));
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ justifyContent: 'flex-end', display: 'flex', mb: 2 }}>
                <ContentButtonAction onClick={handleToggle}>
                  <Iconify icon="mdi:help-circle-outline" sx={{ mr: 1 }} />{' '}
                  {t('mainVisual.preview')}
                </ContentButtonAction>
              </Box>
              <Collapse in={isGuideOpen}>
                <Box sx={{ mb: 3 }}>
                  <Preview
                    formType={formType}
                    areas={getMainVisualAreas()}
                    values={currentPreviewValues}
                  />
                </Box>
              </Collapse>

              <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <FormItem>
                  <RHFSwitch label={t('mainVisual.form.useYn')} name={'useYn'} />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFSelect
                    label={t('mainVisual.form.lang')}
                    name={'langCd'}
                    useAll={false}
                    options={getLang()}
                  />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextFieldWithColor
                    name="mainText"
                    label={t('mainVisual.form.mainText')}
                    onColorChange={handleColorChange('mainText')}
                    selectedColor={colors.mainText}
                  />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextFieldWithColor
                    name="subText"
                    label={t('mainVisual.form.subText')}
                    onColorChange={handleColorChange('subText')}
                    selectedColor={colors.subText}
                  />
                </FormItem>
              </FormRow>
              <FormInnerContainer>
                <FormLabel>{t('mainVisual.form.background')}</FormLabel>
                <FormRow
                  sx={{
                    gridTemplateColumns: '1fr',
                    overflow: 'visible',
                  }}
                >
                  <FormItem>
                    <RHFUploadImage
                      name={'bgPath'}
                      files={files}
                      onChange={handleFileChange}
                      maxFiles={5}
                      accept={{
                        'image/*': [],
                        'video/mp4': [],
                        'video/webm': [],
                      }}
                    />
                  </FormItem>
                </FormRow>
              </FormInnerContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <>
            {formType === FormType.UPDATE && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {formType === FormType.CREATE
                ? `${t('common.button.register')}`
                : `${t('common.button.edit')}`}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
